"use client";

/* -------------------------------------------------------------------------- */
/* ProfileCompletionRing                                                       */
/* -------------------------------------------------------------------------- */

export interface ProfileCompletionRingProps {
  /** Number of completed sections. */
  complete: number;
  /** Total number of sections. */
  total: number;
  /** SVG canvas size in pixels (default `120`). */
  size?: number;
  /** Suppress the "{complete}/{total} Sections Complete" label below the ring. */
  hideLabel?: boolean;
}

/**
 * Animated SVG ring that visualises section-completion progress.
 *
 * - When `complete >= total && total > 0` renders a solid-fill "all done"
 *   state with a checkmark.
 * - Otherwise renders a dual-circle arc ring where the filled arc grows with
 *   `complete / total`.
 */
export function ProfileCompletionRing({
  complete,
  total,
  size = 120,
  hideLabel = false,
}: ProfileCompletionRingProps) {
  const isComplete = total > 0 && complete >= total;
  const pct = total > 0 ? complete / total : 0;

  // Ring geometry: r=50, cx/cy=60 inside a 120×120 viewport
  const cx = size / 2;
  const cy = size / 2;
  const r = size * (50 / 120);
  const strokeWidth = size * (11 / 120);
  const circumference = 2 * Math.PI * r;

  return (
    <div
      className="inline-flex flex-col items-center gap-2"
      role="img"
      aria-label={`${complete} of ${total} sections complete`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="text-(--fg-brand)"
      >
        {isComplete ? (
          /* Solid fill circle + checkmark */
          <>
            <circle
              cx={cx}
              cy={cy}
              r={r + strokeWidth / 2}
              fill="currentColor"
            />
            <path
              d={`M${size * 0.4} ${size * 0.508}L${size * 0.475} ${size * 0.583}L${size * 0.617} ${size * 0.442}`}
              stroke="var(--fg-inverse, #fff)"
              strokeWidth={size * (5 / 120)}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </>
        ) : (
          /* Arc ring */
          <g transform={`rotate(-90 ${cx} ${cy})`}>
            {/* Track */}
            <circle
              cx={cx}
              cy={cy}
              r={r}
              stroke="var(--border-default, #d1d5db)"
              strokeWidth={strokeWidth}
              fill="none"
            />
            {/* Fill arc */}
            <circle
              cx={cx}
              cy={cy}
              r={r}
              stroke="currentColor"
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - pct)}
              style={{
                transition: "stroke-dashoffset 320ms cubic-bezier(0.4,0,0.2,1)",
              }}
            />
          </g>
        )}
      </svg>

      {!hideLabel && (
        <span className="text-xs text-(--fg-secondary) tabular-nums">
          {complete}/{total} Sections Complete
        </span>
      )}
    </div>
  );
}
